import React, { FC, useMemo, useRef, useState } from 'react';
import data, { EmojiMartData } from '@emoji-mart/data';
import Picker from '@emoji-mart/react';

import { ReactComponent as EmojiIcon } from 'applicaiton/assets/emoji.svg';

import { EmojiData, EmojiPickersProps } from './models';
import { EmojiButtonStyled, EmojiContainer, EmojiList } from './styles';
import useOnClickOutside from 'common/hooks/useClickOutside';
import { useLanguage } from 'common/hooks/useLanguage';

/**
 * EmojiPickers Component.
 *
 * A component that renders a button and an emoji picker when clicked.
 * When an emoji is selected from the picker, it triggers a callback function
 * with the native value of the emoji.
 *
 * @param {EmojiPickersProps} props - The props for the component.
 * @returns {React.FC<EmojiPickersProps>} The EmojiPickers component.
 */
const EmojiPickers: FC<EmojiPickersProps> = ({ onSelect, isDisabled }) => {
  const { language } = useLanguage();
  const ref = useRef(null);
  const [isOpened, setIsOpened] = useState(false);

  /**
   * A memoized value that filters out the 'relaxed' emoji from the 'people'
   * category in the emoji data.
   *
   * @returns {EmojiMartData} The filtered emoji data.
   */
  const emojis = useMemo(() => {
    const emojisData = data as EmojiMartData;
    const filteredCategories = emojisData.categories.map((item) => {
      if (item.id === 'people') {
        const filteredEmojis = item.emojis.filter(
          (emoji) => emoji !== 'relaxed',
        );

        return {
          ...item,
          emojis: filteredEmojis,
        };
      }
      return item;
    });

    return { ...emojisData, categories: filteredCategories };
  }, [data]);

  useOnClickOutside(ref, () => setIsOpened(false));

  const handleSelectEmoji = (data: EmojiData) => {
    onSelect(data.native);
  };

  return (
    <EmojiContainer ref={ref}>
      <EmojiButtonStyled
        type="button"
        onClick={() => {
          !isDisabled && setIsOpened((prev) => !prev);
        }}
      >
        <EmojiIcon />
      </EmojiButtonStyled>
      <EmojiList>
        {isOpened && (
          <Picker
            data={emojis}
            onEmojiSelect={handleSelectEmoji}
            locale={language}
            previewPosition={'none'}
          />
        )}
      </EmojiList>
    </EmojiContainer>
  );
};

export default EmojiPickers;
